import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { ArrowLeft, Star, Shield, Clock, CheckCircle, Award, Menu, X, Home, Briefcase, Image, Info, ChevronRight, Phone, MessageCircle, Globe, Sparkles } from 'lucide-react';
import QuoteWizard from '../components/QuoteWizard';

const QuotePage = () => {
  const { language, changeLanguage } = useLanguage();
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const getText = (en, pt) => language === 'pt' ? pt : en;

  const menuItems = [
    { path: '/ie', label: getText('Home', 'Início'), icon: Home },
    { path: '/ie#services', label: getText('Services', 'Serviços'), icon: Briefcase },
    { path: '/ie#gallery', label: getText('Gallery', 'Galeria'), icon: Image },
    { path: '/ie#about', label: getText('About', 'Sobre'), icon: Info },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-gray-100">
      {/* Header */}
      <header className="bg-white shadow-sm">
        <div className="max-w-6xl mx-auto px-4 py-3">
          <div className="flex items-center justify-between">
            {/* Back Button - Desktop only */}
            <button
              onClick={() => navigate('/ie')}
              className="hidden lg:flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors"
            >
              <ArrowLeft size={20} />
              <span>{getText('Back', 'Voltar')}</span>
            </button>
            
            {/* Logo with Name */}
            <Link to="/ie" className="flex items-center lg:absolute lg:left-1/2 lg:-translate-x-1/2">
              <img 
                src="https://customer-assets.emergentagent.com/job_trusted-maintenance-1/artifacts/l9fyih4f_Whisk_4dd066d44f7c3e88b4a47b0cc96820dddr-removebg-preview.png" 
                alt="Sancur Services" 
                className="h-10 sm:h-12"
              />
            </Link>
            
            {/* Right side: Phone + Menu */}
            <div className="flex items-center gap-3">
              <a 
                href="tel:+353899502499"
                className="hidden sm:flex items-center gap-1 text-sm text-gray-600 hover:text-gray-900"
              >
                <Phone size={16} />
                089 950 2499
              </a>
              
              {/* Mobile Menu Toggle - Right side, same style as main header */}
              <button
                onClick={() => setMobileMenuOpen(true)}
                className="lg:hidden p-2 text-[#1a5c3a] rounded-lg hover:bg-gray-100 transition-colors"
                data-testid="quote-mobile-menu-btn"
              >
                <Menu size={24} />
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Mobile Menu Overlay - Same as main Header */}
      <div 
        className={`fixed inset-0 bg-black/50 z-40 transition-opacity duration-300 lg:hidden ${
          mobileMenuOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'
        }`}
        onClick={() => setMobileMenuOpen(false)}
      />

      {/* Mobile Menu Drawer - EXACTLY like main Header */}
      <div 
        className={`fixed top-0 right-0 h-full w-[280px] bg-[#1a5c3a] z-50 transform transition-transform duration-300 ease-out lg:hidden ${
          mobileMenuOpen ? 'translate-x-0' : 'translate-x-full'
        }`}
        data-testid="quote-mobile-menu"
      >
        {/* Menu Header - Logo with company name */}
        <div className="flex items-center justify-between p-4 border-b border-white/10">
          <div className="flex items-center gap-2">
            <img 
              src="https://customer-assets.emergentagent.com/job_trusted-maintenance-1/artifacts/l9fyih4f_Whisk_4dd066d44f7c3e88b4a47b0cc96820dddr-removebg-preview.png" 
              alt="Sancur Services" 
              className="h-10 brightness-0 invert"
            />
            <span className="text-[#d4a84b] font-heading text-lg font-bold tracking-tight">
              Sancur Services
            </span>
          </div>
          <button
            onClick={() => setMobileMenuOpen(false)}
            className="p-2 text-white/70 hover:text-white rounded-lg hover:bg-white/10"
          >
            <X size={24} />
          </button>
        </div>
        
        {/* Menu Items - With arrows and exact same styling */}
        <div className="p-4 space-y-1">
          {menuItems.map((item) => {
            const Icon = item.icon;
            return (
              <Link
                key={item.path}
                to={item.path}
                onClick={() => setMobileMenuOpen(false)}
                className="flex items-center justify-between p-3 rounded-xl text-white/80 hover:bg-white/10 hover:text-white transition-all"
              >
                <div className="flex items-center gap-3">
                  <Icon size={20} />
                  <span className="font-medium">{item.label}</span>
                </div>
                <ChevronRight size={18} className="text-white/50" />
              </Link>
            );
          })}

          {/* Divider */}
          <div className="my-4 border-t border-white/10" />

          {/* Staff Area */}
          <Link
            to="/staff/login"
            onClick={() => setMobileMenuOpen(false)}
            className="flex items-center justify-between p-3 rounded-xl text-white/80 hover:bg-white/10 hover:text-white transition-all"
          >
            <div className="flex items-center gap-3">
              <Briefcase size={20} />
              <span className="font-medium">{getText('Staff Area', 'Área Staff')}</span>
            </div>
            <ChevronRight size={18} className="text-white/50" />
          </Link>

          {/* Back to Home CTA - Golden button */}
          <Link
            to="/ie"
            onClick={() => setMobileMenuOpen(false)}
            className="flex items-center justify-center gap-2 p-4 mt-4 bg-[#d4af37] text-white rounded-xl font-semibold hover:bg-[#c4a030] transition-all"
          >
            <Home size={20} />
            <span>{getText('Back to Home', 'Voltar ao Início')}</span>
          </Link>
        </div>

        {/* Bottom Section - Dark green background */}
        <div className="absolute bottom-0 left-0 right-0 p-4 bg-[#0f3d26]">
          {/* Quick Contact Buttons */}
          <div className="flex gap-2 mb-4">
            <a 
              href="tel:+353899502499"
              className="flex-1 flex items-center justify-center gap-2 p-3 bg-white/10 rounded-xl text-white text-sm hover:bg-white/20 transition-all"
            >
              <Phone size={16} />
              <span>{getText('Call', 'Ligar')}</span>
            </a>
            <a 
              href="https://wa.me/353899502499"
              target="_blank"
              rel="noopener noreferrer"
              className="flex-1 flex items-center justify-center gap-2 p-3 bg-[#25D366] rounded-xl text-white text-sm hover:bg-[#20BA5A] transition-all"
            >
              <MessageCircle size={16} />
              <span>WhatsApp</span>
            </a>
          </div>

          {/* Language Toggle */}
          <div className="flex items-center justify-center gap-2">
            <span className="text-white/50 text-xs mr-2">
              <Globe size={14} className="inline mr-1" />
              {getText('Language:', 'Idioma:')}
            </span>
            <button
              onClick={() => changeLanguage('pt')}
              className={`px-3 py-1.5 rounded-lg text-sm transition-colors ${
                language === 'pt' ? 'bg-white text-[#1a5c3a] font-bold' : 'text-white/70 hover:bg-white/10'
              }`}
            >
              PT
            </button>
            <button
              onClick={() => changeLanguage('en')}
              className={`px-3 py-1.5 rounded-lg text-sm transition-colors ${
                language === 'en' ? 'bg-white text-[#1a5c3a] font-bold' : 'text-white/70 hover:bg-white/10'
              }`}
            >
              EN
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Trust Banner - Only visible on mobile */}
      <div className="lg:hidden bg-white border-b shadow-sm">
        <div className="max-w-6xl mx-auto px-4 py-3">
          <h2 className="text-center text-sm font-semibold text-gray-900 mb-3">
            {getText('Why Choose Sancur?', 'Porquê Escolher a Sancur?')}
          </h2>
          <div className="grid grid-cols-2 gap-2">
            <div className="flex items-center gap-2 bg-green-50 rounded-lg p-2">
              <Shield className="text-green-600 flex-shrink-0" size={16} />
              <span className="text-xs text-gray-700">{getText('Fully Insured', 'Totalmente Segurado')}</span>
            </div>
            <div className="flex items-center gap-2 bg-blue-50 rounded-lg p-2">
              <Clock className="text-blue-600 flex-shrink-0" size={16} />
              <span className="text-xs text-gray-700">{getText('Quote in 2h', 'Orçamento em 2h')}</span>
            </div>
            <div className="flex items-center gap-2 bg-yellow-50 rounded-lg p-2">
              <Star className="text-yellow-600 flex-shrink-0" size={16} />
              <span className="text-xs text-gray-700">{getText('5-Star Reviews', 'Avaliações 5 Estrelas')}</span>
            </div>
            <div className="flex items-center gap-2 bg-purple-50 rounded-lg p-2">
              <CheckCircle className="text-purple-600 flex-shrink-0" size={16} />
              <span className="text-xs text-gray-700">{getText('Satisfaction Guaranteed', 'Satisfação Garantida')}</span>
            </div>
          </div>
          {/* Mini testimonial */}
          <div className="mt-3 flex items-center justify-center gap-2 text-xs text-gray-500">
            <div className="flex gap-0.5">
              {[1,2,3,4,5].map(i => (
                <Star key={i} size={10} className="fill-yellow-400 text-yellow-400" />
              ))}
            </div>
            <span>&ldquo;{getText('Excellent service!', 'Serviço excelente!')}&rdquo; — Sarah M.</span>
          </div>
        </div>
      </div>

      <main className="max-w-6xl mx-auto px-4 py-4 lg:py-8">
        <div className="grid lg:grid-cols-3 gap-8">
          {/* Left Column - Benefits (Hidden on mobile, shown above) */}
          <div className="hidden lg:block lg:col-span-1 space-y-6">
            {/* Hero Text */}
            <div className="text-center lg:text-left">
              <h1 className="text-3xl font-bold text-gray-900 mb-4">
                {getText(
                  'Get your free quote for residential cleaning.',
                  'Solicite seu orçamento gratuito para limpeza residencial.'
                )}
              </h1>
              <p className="text-gray-600">
                {getText(
                  "Answer a few quick questions about your home and we'll finalise the booking with you via WhatsApp. No payment now. You only pay after the cleaning is done.",
                  "Responda algumas perguntas rápidas sobre sua casa e finalizaremos a reserva com você via WhatsApp. Nenhum pagamento agora. Você só paga após a limpeza."
                )}
              </p>
            </div>

            {/* Trust Badges */}
            <div className="bg-white rounded-2xl shadow-sm p-6 space-y-4">
              <h3 className="font-semibold text-gray-900">
                {getText('Why Choose Sancur?', 'Porquê Escolher a Sancur?')}
              </h3>
              
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center flex-shrink-0">
                  <Shield className="text-green-600" size={20} />
                </div>
                <div>
                  <p className="font-medium text-gray-900">
                    {getText('Fully Insured', 'Totalmente Segurado')}
                  </p>
                  <p className="text-sm text-gray-500">
                    {getText('Peace of mind for your home', 'Tranquilidade para a sua casa')}
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-3">
                <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
                  <Clock className="text-blue-600" size={20} />
                </div>
                <div>
                  <p className="font-medium text-gray-900">
                    {getText('Fast Response', 'Resposta Rápida')}
                  </p>
                  <p className="text-sm text-gray-500">
                    {getText('Quote within 2 hours', 'Orçamento em 2 horas')}
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-3">
                <div className="w-10 h-10 bg-yellow-100 rounded-lg flex items-center justify-center flex-shrink-0">
                  <Star className="text-yellow-600" size={20} />
                </div>
                <div>
                  <p className="font-medium text-gray-900">
                    {getText('5-Star Reviews', 'Avaliações 5 Estrelas')}
                  </p>
                  <p className="text-sm text-gray-500">
                    {getText('Trusted by Dublin families', 'Confiado por famílias de Dublin')}
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-3">
                <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center flex-shrink-0">
                  <CheckCircle className="text-purple-600" size={20} />
                </div>
                <div>
                  <p className="font-medium text-gray-900">
                    {getText('Satisfaction Guaranteed', 'Satisfação Garantida')}
                  </p>
                  <p className="text-sm text-gray-500">
                    {getText("Not happy? We'll redo it free", 'Não ficou satisfeito? Refazemos grátis')}
                  </p>
                </div>
              </div>
            </div>

            {/* Testimonial */}
            <div className="bg-white rounded-2xl shadow-sm p-6">
              <div className="flex gap-1 mb-3">
                {[1,2,3,4,5].map(i => (
                  <Star key={i} size={16} className="fill-yellow-400 text-yellow-400" />
                ))}
              </div>
              <p className="text-gray-600 italic mb-4">
                &ldquo;{getText(
                  "Excellent service! The team was professional and my house has never been cleaner. Highly recommend!",
                  "Serviço excelente! A equipa foi profissional e a minha casa nunca esteve tão limpa. Recomendo muito!"
                )}&rdquo;
              </p>
              <p className="text-sm text-gray-500">— Sarah M., Dublin 2</p>
            </div>
          </div>

          {/* Right Column - Form */}
          <div className="lg:col-span-2">
            <QuoteWizard language={language} />
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="bg-white border-t mt-12 py-6">
        <div className="max-w-6xl mx-auto px-4 text-center text-sm text-gray-500">
          <p>© 2024 Sancur Property Services. {getText('All rights reserved.', 'Todos os direitos reservados.')}</p>
          <p className="mt-2">
            {getText('No credit card required. We only use your details to send the quote.', 
                    'Não é necessário cartão de crédito. Usaremos seus dados apenas para enviar o orçamento.')}
          </p>
        </div>
      </footer>
    </div>
  );
};

export default QuotePage;
